import React from 'react';
import MaskedInput from 'react-text-mask';

import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import MultipleSelect from '@/components/OrdersReportsModal/components/MultipleSelect';
import ClientsSelect from '@/components/OrdersReportsModal/components/ClientsSelect';
import { DATE_MASK } from '@/helpers/mask';

const OrdersReportsModalComponent = ({ types, statuses, inspectors, handleSubmit, intl }) => (
  <div className="">
    <h3 className="m-0 mb-2 text-[1.25rem] font-bold text-[#393939]">
      {intl.formatMessage({ id: 'EXPORT_EXTRACT' })}
    </h3>

    <p className="m-0 mb-3 text-[0.85rem] text-[#393939]">
      {intl.formatMessage({ id: 'ORDERS_REPORTS_FORM_INSTRUCTION' })}
    </p>

    <Formik
      initialValues={{
        clients: [],
        types: [],
        statuses: [],
        inspectors: [],
        minCreatedAt: '',
        maxCreatedAt: '',
        minFirstSentAt: '',
        maxFirstSentAt: '',
      }}
      onSubmit={handleSubmit}
    >
      {({ errors, isSubmitting, handleChange, setFieldValue }) => (
        <Form>
          <div className="flex flex-col mt-[1em] first:mt-0">
            <label htmlFor="clients" className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]">
              {intl.formatMessage({ id: 'CUSTOMERS' })}
            </label>

            <ClientsSelect handleSelectClient={values => setFieldValue('clients', values || [])} />

            {errors.clients && <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.clients}</div>}
          </div>

          <div className="flex flex-col mt-[1em]">
            <label htmlFor="types" className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]">
              {intl.formatMessage({ id: 'TYPES' })}
            </label>

            <Field
              name="types"
              id="types"
              options={types}
              placeholder={intl.formatMessage({ id: 'TYPES' })}
              component={props => <MultipleSelect {...props} />}
            />

            {errors.types && <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.types}</div>}
          </div>

          <div className="flex flex-col mt-[1em]">
            <label htmlFor="statuses" className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]">
              {intl.formatMessage({ id: 'STATUS' })}
            </label>

            <Field
              name="statuses"
              id="statuses"
              options={statuses}
              placeholder={intl.formatMessage({ id: 'STATUS' })}
              component={props => <MultipleSelect {...props} />}
            />

            {errors.statuses && <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.statuses}</div>}
          </div>

          <div className="flex flex-col mt-[1em]">
            <label htmlFor="inspectors" className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]">
              {intl.formatMessage({ id: 'INSPECTORS' })}
            </label>

            <Field
              name="inspectors"
              id="inspectors"
              options={inspectors}
              placeholder={intl.formatMessage({ id: 'INSPECTORS' })}
              component={props => <MultipleSelect {...props} />}
            />

            {errors.inspectors && <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.inspectors}</div>}
          </div>

          <div className="mt-[1.25em]">
            <label className="block mb-2 text-[0.75rem] font-bold uppercase text-[#393939]">
              {intl.formatMessage({ id: 'LABEL.PERIOD' })}
            </label>

            <section className="flex gap-4 mb-2 w-full justify-between">
              <div className="flex flex-col flex-1">
                <label
                  htmlFor="minCreatedAt"
                  className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]"
                >
                  {intl.formatMessage({ id: 'FROM' })} {intl.formatMessage({ id: 'REQUESTED_AT' })}
                </label>

                <MaskedInput
                  id="minCreatedAt"
                  name="minCreatedAt"
                  type="text"
                  className="h-[3em] px-2 border border-[#e3e3e3] rounded-[3px] focus:border-[#4d8fca] focus:outline-none text-[0.875em] font-semibold text-[#919191]"
                  onChange={handleChange}
                  mask={DATE_MASK}
                  guide={false}
                />

                {errors.minCreatedAt && (
                  <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.minCreatedAt}</div>
                )}
              </div>

              <div className="flex flex-col flex-1">
                <label htmlFor="maxCreatedAt" className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]">
                  {intl.formatMessage({ id: 'UNTIL' })}
                </label>

                <MaskedInput
                  id="maxCreatedAt"
                  name="maxCreatedAt"
                  type="text"
                  className="h-[3em] px-2 border border-[#e3e3e3] rounded-[3px] focus:border-[#4d8fca] focus:outline-none text-[0.875em] font-semibold text-[#919191]"
                  onChange={handleChange}
                  mask={DATE_MASK}
                  guide={false}
                />

                {errors.maxCreatedAt && (
                  <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.maxCreatedAt}</div>
                )}
              </div>
            </section>

            <section className="flex gap-4 w-full justify-between mt-2">
              <div className="flex flex-col flex-1">
                <label
                  htmlFor="minFirstSentAt"
                  className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]"
                >
                  {intl.formatMessage({ id: 'FROM' })} {intl.formatMessage({ id: 'DELIVERED_AT' })}
                </label>

                <MaskedInput
                  id="minFirstSentAt"
                  name="minFirstSentAt"
                  type="text"
                  className="h-[3em] px-2 border border-[#e3e3e3] rounded-[3px] focus:border-[#4d8fca] focus:outline-none text-[0.875em] font-semibold text-[#919191]"
                  onChange={handleChange}
                  mask={DATE_MASK}
                  guide={false}
                />

                {errors.minFirstSentAt && (
                  <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.minFirstSentAt}</div>
                )}
              </div>

              <div className="flex flex-col flex-1">
                <label htmlFor="maxFirstSentAt" className="mb-1.5 text-[0.75rem] font-bold uppercase text-[#393939]">
                  {intl.formatMessage({ id: 'UNTIL' })}
                </label>

                <MaskedInput
                  id="maxFirstSentAt"
                  name="maxFirstSentAt"
                  type="text"
                  className="h-[3em] px-2 border border-[#e3e3e3] rounded-[3px] focus:border-[#4d8fca] focus:outline-none text-[0.875em] font-semibold text-[#919191]"
                  onChange={handleChange}
                  mask={DATE_MASK}
                  guide={false}
                />

                {errors.maxFirstSentAt && (
                  <div className="mt-2 text-[0.87rem] text-[#ed0000]">{errors.maxFirstSentAt}</div>
                )}
              </div>
            </section>
          </div>

          <div className="mt-[1.25em] flex justify-end items-center">
            <button
              className="h-[36px] px-[1.2rem] min-w-[140px] bg-[#97a500] text-white rounded-[3px] font-bold text-[0.9375rem] transition-opacity duration-300 hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
              type="submit"
              disabled={isSubmitting}
            >
              {intl.formatMessage({ id: 'EXPORT_EXTRACT' })}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  </div>
);

OrdersReportsModalComponent.propTypes = {
  types: PropTypes.array.isRequired,
  statuses: PropTypes.array.isRequired,
  inspectors: PropTypes.array.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

export default OrdersReportsModalComponent;
